<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';

export default {
  components: {
    LoadingState,
    SettingsSection,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      gupshupAccountDetails: null,
      videoUrl: '/video/MM_LITE.mp4',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    phoneQualityLabel() {
      const key = this.gupshupAccountDetails?.phoneQuality || 'UNKNOWN';
      return this.$t(`INBOX_MGMT.META_INFORMATION.${key}`);
    },
    messagingLimitDisplay() {
      const tier = this.gupshupAccountDetails?.messagingLimit || 'TIER_NOT_SET';
      const mapping = {
        TIER_50: '50',
        TIER_250: '250',
        TIER_1K: '1,000',
        TIER_2K: '2,000',
        TIER_10K: '10,000',
        TIER_100K: '100,000',
        TIER_NOT_SET: this.$t('INBOX_MGMT.META_INFORMATION.TIER_NOT_SET'),
        TIER_UNLIMITED: this.$t('INBOX_MGMT.META_INFORMATION.TIER_UNLIMITED'),
      };
      return mapping[tier] || this.$t('INBOX_MGMT.META_INFORMATION.UNKNOWN');
    },
  },
  mounted() {
    this.$store
    .dispatch('inboxes/fetchGupshupAccountDetails', this.inbox.id)
    .then(response => {
      this.gupshupAccountDetails = response.payload;
      if (this.gupshupAccountDetails?.mmLiteLinkError) {
        console.error('MM Lite link error:', this.gupshupAccountDetails.mmLiteLinkError);
      }
    })
    .catch(error => {
      console.error('Failed to fetch META account details:', error);
    });
  },
  methods: {
    openMMLiteLink() {
      if (this.gupshupAccountDetails?.mmLiteLink) {
        window.open(this.gupshupAccountDetails.mmLiteLink, '_blank');
      } else {
        console.warn(this.gupshupAccountDetails.mmLiteLinkError);
      }
    },
  },
};
</script>

<template>
  <div class="mx-4">
    <SettingsSection
      :title="$t('INBOX_MGMT.META_INFORMATION.TITLE')"
      :sub-title="$t('INBOX_MGMT.META_INFORMATION.SUBTITLE')"
    >
      <div class="settings-item">
        <div v-if="uiFlags.isFetching">
          <LoadingState />
        </div>

        <div v-else-if="gupshupAccountDetails">
          <div class="card-container text-slate-700 dark:text-slate-300 w-full lg:w-[150%]">
            <div class="card-content">
              <div class="card-header">
                <label class="card-title title-size text-woot-500 dark:text-woot-500"><p>
                {{ $t('INBOX_MGMT.META_INFORMATION.WABA_ACCOUNT_DETAILS') }}
                </p></label>
              </div>
              <div class="card-footer grid grid-cols-2 gap-4">
                <label class="card-label">
                  <p class="flex items-center gap-2">{{ $t('INBOX_MGMT.META_INFORMATION.PHONE_NUMBER') }}</p>
                  <woot-code :script="this.inbox.settings.number"/>
                </label>
                <label class="card-label">
                  <p class="flex items-center gap-2">{{ $t('INBOX_MGMT.META_INFORMATION.WABA_ID') }}</p>
                  <woot-code :script="gupshupAccountDetails?.wabaId"/>
                </label>
              </div>
              <div class="card-footer">
                <label class="card-label">
                  <p>{{ $t('INBOX_MGMT.META_INFORMATION.DISPLAY_NAME') }} <br/> <span style="font-weight: bold;">{{ gupshupAccountDetails?.verifiedName }}</span></p>
                </label>
              </div>
              <div class="card-footer">
                <label class="card-label">
                  <p>{{ $t('INBOX_MGMT.META_INFORMATION.BUSINESS_ENTITY_NAME') }} <br/> <span style="font-weight: bold;">{{ gupshupAccountDetails?.wabaName }}</span></p>
                </label>
              </div>
              <div class="card-footer">
                <label class="card-label">
                  <p style="white-space: pre-line;">{{ $t('INBOX_MGMT.META_INFORMATION.TIMEZONE') }} <br/> <span style="font-weight: bold;">{{ gupshupAccountDetails?.timezone }}</span></p>
                </label>
              </div>
            </div>
          </div>

          <br/>

          <div class="card-container text-slate-700 dark:text-slate-300 w-full lg:w-[150%]">
            <div class="card-content">
              <div class="card-header">
                <label class="card-title title-size text-woot-500 dark:text-woot-500"><p>
                {{ $t('INBOX_MGMT.META_INFORMATION.WABA_HEALTH') }}
                </p></label>
              </div>
              <div class="card-footer grid grid-cols-2 gap-4">
                <label class="card-label">
                  <p>{{ $t('INBOX_MGMT.META_INFORMATION.ACCOUNT_STATUS') }}<br/> 
                  <span style="font-weight: bold;" :class="`status-badge status-badge__${gupshupAccountDetails?.accountStatus} rounded-full w-2.5 h-2.5 mr-0.5 rtl:mr-0 rtl:ml-0.5 inline-flex`"/>
                  <span :class="`status-text status-text__${gupshupAccountDetails?.accountStatus}`"> {{ $t(`INBOX_MGMT.META_INFORMATION.${gupshupAccountDetails?.accountStatus}`) }} </span>
                  </p>
                </label>
                <label class="card-label">
                  <p>{{ $t('INBOX_MGMT.META_INFORMATION.DOCKER_STATUS') }}<br/> 
                  <div :class="`status-badge status-badge__${gupshupAccountDetails?.dockerStatus} rounded-full w-2.5 h-2.5 mr-0.5 rtl:mr-0 rtl:ml-0.5 inline-flex`"/>
                  <span style="font-weight: bold;" :class="`status-text status-text__${gupshupAccountDetails?.dockerStatus}`"> {{ $t(`INBOX_MGMT.META_INFORMATION.${gupshupAccountDetails?.dockerStatus}`) }} </span>
                  </p>
                </label>
              </div>
              <div class="card-footer grid grid-cols-2 gap-4">
                <label class="card-label">
                  <p>{{ $t('INBOX_MGMT.META_INFORMATION.PHONE_QUALITY') }}<br/> 
                  <div :class="`status-badge status-badge__${gupshupAccountDetails?.phoneQuality} rounded-full w-2.5 h-2.5 mr-0.5 rtl:mr-0 rtl:ml-0.5 inline-flex`"/>
                  <span style="font-weight: bold;" :class="`status-text status-text__${gupshupAccountDetails?.phoneQuality}`"> {{ phoneQualityLabel }} </span>
                  </p>
                </label>
                <label class="card-label">
                  <p>{{ $t('INBOX_MGMT.META_INFORMATION.MESSAGING_LIMIT') }} <br/> <span style="font-weight: bold;">{{ messagingLimitDisplay }} {{ $t('INBOX_MGMT.META_INFORMATION.CONTACTS') }}</span></p>
                </label>
              </div>
            </div>
          </div>

          <br/>

          <div class="card-container text-slate-700 dark:text-slate-300 w-full lg:w-[150%]">
            <div class="card-content">
              <div class="card-footer grid grid-cols-2 gap-4">
                <label class="card-label">
                  <p>{{ $t('INBOX_MGMT.META_INFORMATION.MML_STATUS') }}: 
                  <span style="font-weight: bold;" :class="`status-text status-text__${gupshupAccountDetails?.mmLiteStatus}`">
                    {{ $t(`INBOX_MGMT.META_INFORMATION.${gupshupAccountDetails?.mmLiteStatus}`) }}
                  </span>
                  </p>
                </label>
                <woot-submit-button
                  v-if="
                    gupshupAccountDetails &&
                    gupshupAccountDetails.mmLiteStatus === 'ELIGIBLE' &&
                    gupshupAccountDetails.mmLiteLink !== null &&
                    gupshupAccountDetails.mmLiteLinkError === null
                  "
                  type="button"
                  :disabled="uiFlags.isFetching"
                  :button-text="$t('INBOX_MGMT.META_INFORMATION.MML_BUTTON')"
                  :loading="uiFlags.isFetching"
                  @click="openMMLiteLink"
                  class="justify-self-start"
                />
              </div>
              <div class="card-footer">
                <div v-if="gupshupAccountDetails?.mmLiteStatus === 'ELIGIBLE'" class="flex justify-center">
                  <video
                    controls
                    :src="videoUrl"
                    class="max-w-full"
                    style="display: inline-block; width: auto; height: 200px;"
                  />
                </div>
              </div>
            </div>
          </div>

          
        </div>

        <div v-else>
          <p>No se encontraron detalles para este inbox.</p>
        </div>
      </div>
    </SettingsSection>
  </div>
</template>

<style scoped lang="scss">
@import 'widget/assets/scss/variables.scss';
@import 'widget/assets/scss/mixins.scss';

.flex-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
}

.space-left {
  margin-left: 12px;
  width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
}

.card-container {
  display: flex;
  flex-direction: column;
  border: 1px solid #e5eaf0 !important;
  border-radius: 8px;
  background-color: #f8fafc;
  width: 100%;
}

.card-content {
  padding: 16px;
  p {
    margin: 0;
  }
}

.card-header {
  justify-content: space-between;
}

.card-title {
  font-weight: bold;
  p {
    text-overflow: ellipsis;
    overflow: hidden;
  }
}

.card-subtitle {
  font-size: 14px;
  color: #666;
}

.card-footer {
  margin-top: 12px;
}

.card-label {
  font-size: 12px;
  margin-bottom: 5px;
}

.status-badge {
  &__ACTIVE {
    background-color: #4DC558;
  }
  &__BANNED {
    background-color: #FF0000;
  }
  &__CONNECTED {
    background-color: #4DC558;
  }
  &__DISCONNECTED {
    background-color: #FF0000;
  }
  &__FLAGGED {
    background-color: #FF0000;
  }
  &__PENDING {
    @apply bg-yellow-500;
  }
  &__RESTRICTED {
    background-color: #FF0000;
  }
  &__UNKNOWN {
    @apply bg-slate-500;
  }
  &__GREEN {
    background-color: #4DC558;
  }
  &__RED {
    background-color: #FF0000;
  }
  &__YELLOW {
    @apply bg-yellow-500;
  }
}

.status-text {
  font-weight: bold;
  &__ACTIVE {
    color: #4DC558;
  }
  &__BANNED {
    color: #FF0000;
  }
  &__CONNECTED {
    color: #4DC558;
  }
  &__DISCONNECTED {
    color: #FF0000;
  }
  &__FLAGGED {
    color: #FF0000;
  }
  &__PENDING {
    color: #EAB308;
  }
  &__RESTRICTED {
    color: #FF0000;
  }
  &__UNKNOWN {
    color: #6B7280;
  }
  &__GREEN {
    color: #4DC558;
  }
  &__RED {
    color: #FF0000;
  }
  &__YELLOW {
    color: #EAB308;
  }
  &__ELIGIBLE {
    color: #4DC558;
  }
  &__INELIGIBLE {
    color: #FF0000;
  }
  &__ONBOARDED {
    color: #1F93FF;
  }
}
</style>
